import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './webstoreAdmin.scss';
import { toast } from 'react-toastify';
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  Select, MenuItem, Tooltip, Chip, TextField, TablePagination, IconButton
} from '@mui/material';
import WebstoreOrderModal from './WebstoreOrderDetailsModal';
import VisibilityIcon from '@mui/icons-material/Visibility';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const WebstoreAdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/webstore-orders`, {
        headers: { 'x-admin-auth': 'navarrojuben' },
      });
      setOrders(res.data);
    } catch {
      toast.error('Failed to fetch orders');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/webstore-orders/${orderId}`,
        { status: newStatus },
        { headers: { 'x-admin-auth': 'navarrojuben' } }
      );
      toast.success('Order status updated');
      fetchOrders();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const statusPriority = {
    processing: 1,
    pending: 2,
    completed: 3,
    cancelled: 4,
  };

  const statusColor = {
    pending: 'warning',
    processing: 'info',
    completed: 'success',
    cancelled: 'error',
  };

  const filteredOrders = orders
    .filter((order) =>
      (order.user?.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.user?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order._id?.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (statusFilter === 'all' || order.status === statusFilter)
    )
    .sort((a, b) => statusPriority[a.status] - statusPriority[b.status]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <div className="webstore-admin-orders-wrapper">
      <h2>Manage Orders</h2>

      <div className="order-search-bar" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        <TextField
          label="Search by user, email, or ID"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          className="status-filter-dropdown"
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </Select>

      </div>

      <div className="table-wrapper">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Order Info</TableCell>
              <TableCell>Services</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredOrders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow key={order._id}>
                  <TableCell>
                    <strong>{order.user?.username}</strong><br />
                    {order.user?.email}
                  </TableCell>

                  <TableCell>
                    <p><strong>Order ID:</strong> {order._id}</p>
                    <p><strong>Payment:</strong> {order.paymentMethod || 'N/A'}</p>
                    <p><strong>Note:</strong> {order.note || '—'}</p>
                  </TableCell>

                  <TableCell>
                    {order.items.map((item, idx) => (
                      <div key={idx} className="order-item">
                        <Tooltip title={item.description || ''}>
                          <div><strong>{item.title}</strong> – ₱{item.price.toFixed(2)}</div>
                        </Tooltip>
                        <small>{item.category} • {item.deliveryTime || 3} day(s)</small>
                      </div>
                    ))}
                  </TableCell>

                  <TableCell className="order-total">
                    ₱{order.total.toFixed(2)}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={order.status}
                      color={statusColor[order.status] || 'default'}
                      size="small"
                    />
                    <Select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order._id, e.target.value)}
                      size="small"
                      className="status-select"
                      fullWidth
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="processing">Processing</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    {new Date(order.createdAt).toLocaleString()}
                  </TableCell>

                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        onClick={() => setSelectedOrder(order)}
                        size="small"
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredOrders.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </div>

      {selectedOrder && (
        <WebstoreOrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
};

export default WebstoreAdminOrders;
